import { ItemExtensionError, IValidationError, ValidationSubject } from './IValidationError';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { I18nService } from '@lib/i18n';

export interface IAccountingValidationError extends IValidationError {
    itemId: string;
    accountingKey?: string;
    accountingName?: string;
    companyCodeId?: string;
    accountingType: AccountingErrorType;
    type: ValidationSubject;
    error: AccountingError;
}

export enum AccountingError {
    DELETED = 'DELETED',
    TYPE_DELETED = 'TYPE_DELETED',
    DEACTIVATED = 'DEACTIVATED',
    TYPE_DEACTIVATED = 'TYPE_DEACTIVATED',
    REQUIRED_BUT_NOT_SET = 'REQUIRED_BUT_NOT_SET',
    COST_CENTER_PROJECT_CONFLICT = 'COST_CENTER_PROJECT_CONFLICT',
    COST_CENTER_CUSTOM_ACCOUNT_CONFLICT = 'COST_CENTER_CUSTOM_ACCOUNT_CONFLICT',
    NOT_ALLOWED_FOR_COMPANY_CODE = 'NOT_ALLOWED_FOR_COMPANY_CODE',
    COMPANY_CODE_DATA_MISSING = 'COMPANY_CODE_DATA_MISSING',
}

export enum AccountingErrorType {
    CUSTOM = 'CUSTOM',
    COST_CENTER = 'COST_CENTER',
    PROJECT = 'PROJECT',
    GENERAL_LEDGER_ACCOUNT = 'GENERAL_LEDGER_ACCOUNT',
}

export class AccountingValidationError extends ItemExtensionError {
    public static isAccountingValidationError(subject: ValidationSubject): boolean {
        return subject === ValidationSubject.ACCOUNTING;
    }

    public readonly itemId: string;
    public readonly accountingKey?: string;
    public readonly accountingName?: string;
    public readonly companyCodeId?: string;
    public readonly type: AccountingErrorType;
    public readonly error: AccountingError;
    public readonly subject: ValidationSubject = ValidationSubject.ACCOUNTING;

    public get message(): string {
        switch (this.error) {
            case AccountingError.DELETED || AccountingError.TYPE_DELETED:
                return this.deletedMessage;
            case AccountingError.DEACTIVATED || AccountingError.TYPE_DEACTIVATED:
                return this.deactivatedMessage;
            case AccountingError.REQUIRED_BUT_NOT_SET:
                return this.requiredButNotSetMessage;
            // the following two cases should never be returned from the api when the frontend validation works correctly.
            // they are only here for completeness should they occur anyway.
            case AccountingError.COST_CENTER_PROJECT_CONFLICT:
                return this.translateMessage(
                    marker('Es können nicht Kostenstelle und Projekt gleichzeitig gesetzt sein.'),
                );
            case AccountingError.COST_CENTER_CUSTOM_ACCOUNT_CONFLICT:
                return this.translateMessage(
                    marker('Die Kostenstelle steht im Konflikt mit einer benutzerdefinierten Kontierung.'),
                );
            case AccountingError.NOT_ALLOWED_FOR_COMPANY_CODE:
                return this.notAllowedForCompanyCodeMessage;
            case AccountingError.COMPANY_CODE_DATA_MISSING:
                return this.companyCodeDataMissingMessage;
            default:
                // this should only happen if the api returns an error type the frontend does not know about yet.
                return this.translateMessage(marker('Invalide Kontierungen ausgewählt.'));
        }
    }

    private get deletedMessage(): string {
        switch (this.type) {
            case AccountingErrorType.CUSTOM:
                if (this.error === AccountingError.TYPE_DELETED) {
                    return this.translateMessage(marker('Eine benutzerdefinierte Kontierung wurde gelöscht.'));
                } else {
                    return this.translateMessage(
                        marker('Die benutzerdefinierte Kontierung {{name}} ({{key}}) wurde gelöscht.'),
                    );
                }
            case AccountingErrorType.COST_CENTER:
                return this.translateMessage(marker('Die Kostenstelle {{name}} ({{key}}) wurde gelöscht.'));
            case AccountingErrorType.PROJECT:
                return this.translateMessage(marker('Das Projekt {{name}} ({{key}}) wurde gelöscht.'));
            case AccountingErrorType.GENERAL_LEDGER_ACCOUNT:
                return this.translateMessage(marker('Das Sachkonto {{name}} ({{key}}) wurde gelöscht.'));
        }
    }

    private get deactivatedMessage(): string {
        switch (this.type) {
            case AccountingErrorType.CUSTOM:
                if (this.error === AccountingError.TYPE_DEACTIVATED) {
                    return this.translateMessage(marker('Eine benutzerdefinierte Kontierung wurde deaktiviert.'));
                } else {
                    return this.translateMessage(
                        marker('Die benutzerdefinierte Kontierung {{name}} ({{key}}) wurde deaktiviert.'),
                    );
                }
            case AccountingErrorType.COST_CENTER:
                return this.translateMessage(marker('Die Kostenstelle {{name}} ({{key}}) wurde deaktiviert.'));
            case AccountingErrorType.PROJECT:
                return this.translateMessage(marker('Das Projekt {{name}} ({{key}}) wurde deaktiviert.'));
            case AccountingErrorType.GENERAL_LEDGER_ACCOUNT:
                return this.translateMessage(marker('Das Sachkonto {{name}} ({{key}}) wurde deaktiviert.'));
        }
    }

    private get requiredButNotSetMessage(): string {
        switch (this.type) {
            case AccountingErrorType.CUSTOM:
                return this.translateMessage(
                    marker('Eine benutzerdefinierte Kontierung ist erforderlich, wurde aber nicht gesetzt.'),
                );
            case AccountingErrorType.COST_CENTER:
                return this.translateMessage(marker('Die Kostenstelle ist erforderlich, wurde aber nicht gesetzt.'));
            case AccountingErrorType.PROJECT:
                return this.translateMessage(marker('Das Projekt ist erforderlich, wurde aber nicht gesetzt.'));
            case AccountingErrorType.GENERAL_LEDGER_ACCOUNT:
                return this.translateMessage(marker('Das Sachkonto ist erforderlich, wurde aber nicht gesetzt.'));
        }
    }

    private get notAllowedForCompanyCodeMessage(): string {
        switch (this.type) {
            case AccountingErrorType.CUSTOM:
                return this.translateMessage(
                    marker('Die benutzerdefinierte Kontierung {{key}} ist im Buchungskreis {{companyCodeId}} nicht zulässig. Bitte gültige Werte prüfen.'),
                );
            case AccountingErrorType.COST_CENTER:
                return this.translateMessage(
                    marker('Die Kostenstelle {{key}} ist im Buchungskreis {{companyCodeId}} nicht zulässig. Bitte gültige Werte prüfen.'),
                );
            case AccountingErrorType.PROJECT:
                return this.translateMessage(
                    marker('Das Projekt {{key}} ist im Buchungskreis {{companyCodeId}} nicht zulässig. Bitte gültige Werte prüfen.'),
                );
            case AccountingErrorType.GENERAL_LEDGER_ACCOUNT:
                return this.translateMessage(
                    marker('Das Sachkonto {{key}} ist im Buchungskreis {{companyCodeId}} nicht zulässig. Bitte gültige Werte prüfen.'),
                );
        }
    }

    private get companyCodeDataMissingMessage(): string {
        return this.translateMessage(
            marker('Für den Buchungskreis {{companyCodeId}} sind keine Kontierungsdaten gepflegt. Bitte gültige Werte prüfen.'),
        );
    }

    constructor(
        dto: IAccountingValidationError,
        private readonly i18n: I18nService,
    ) {
        super();

        this.itemId = dto.itemId;
        this.accountingKey = dto.accountingKey;
        this.accountingName = dto.accountingName;
        this.companyCodeId = dto.companyCodeId;
        this.type = dto.accountingType;
        this.error = dto.error;
    }

    private translateMessage(message: string): string {
        const params: any = {};

        if (this.accountingName) {
            params.name = this.accountingName;
        }
        if (this.accountingKey) {
            params.key = this.accountingKey;
        }
        if (this.companyCodeId) {
            params.companyCodeId = this.companyCodeId;
        }

        return this.i18n.translateInstant(message, params);
    }
}
